import {
    faBorderAll,
    faChalkboardTeacher,
    faFileDownload,
    faMapPin,
    faPaste,
    faPowerOff,
    faTags,
    faTicketAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { useDownload } from '@hooks/useDownload';
import useModalManager from '@hooks/useModalManager';
import { useClubSlug } from 'components/Clubs/hooks';
import { PaymentStatusButton } from 'components/MollieConnect';
import { convertBlockCode } from 'components/Vouchers/helpers';
import { Voucher } from 'components/Vouchers/types';
import { VoucherValueManager } from 'components/Vouchers/VoucherList/VoucherList';
import VoucherStatus from 'components/Vouchers/VoucherList/VoucherStatus';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, DataTable, DataTableContainer, TableActionButton } from '../../../DataTable';
import { ClubMember } from '../../types';

interface Props {
    member: ClubMember;
}

const ClubMemberVoucherList = ({ member }: Props) => {
    const { t } = useTranslation('member-management');
    const clubSlug = useClubSlug();
    const { addModal, removeModal } = useModalManager();
    const asyncListRef = useRef(null);
    const download = useDownload();
    const api = useApi();

    const renderScopeIcons = useCallback((voucher: Voucher) => {
        const icons = [];
        if (voucher.scopes.includes('events')) icons.push(faTicketAlt);
        if (voucher.scopes.includes('space_bookings')) icons.push(faMapPin);
        if (voucher.scopes.includes('space_manager')) icons.push(faBorderAll);
        if (voucher.scopes.includes('trainer_bookings')) icons.push(faChalkboardTeacher);
        return icons;
    }, []);

    const renderTypeName = useCallback(
        (voucher: Voucher) => {
            switch (voucher.type) {
                case 'discount':
                    return t('clubMember.preview.vouchers.type.discount');
                case 'card':
                    return t('clubMember.preview.vouchers.type.card');
                default:
                    return t('clubMember.preview.vouchers.type.value');
            }
        },
        [t]
    );

    const renderCode = useCallback((voucher: Voucher) => {
        switch (voucher.type) {
            case 'discount':
                return voucher.code?.toUpperCase();
            default:
                return convertBlockCode(voucher.code);
        }
    }, []);

    const handleToggleVoucherStatus = useCallback((voucher: Voucher) => {
        void api({
            url: `club/${clubSlug}/voucher/${voucher.id}/toggle`,
            method: 'GET',
            onSuccess: () => asyncListRef?.current?.reload()
        });
    }, []);

    const columns: Column<Voucher>[] = useMemo(
        () => [
            {
                uid: 'code',
                name: t('clubMember.preview.vouchers.columns.code'),
                renderer: (code, item) => (
                    <div className="tw-flex tw-flex-col">
                        <span className="tw-text-xs tw-italic tw-text-accents-6">
                            # {item.id}{' '}
                            {item.refund
                                ? t('clubMember.preview.vouchers.refundAbbr')
                                : t('clubMember.preview.vouchers.purchaseAbbr')}{' '}
                            ({renderTypeName(item)})
                        </span>
                        <span className="tw-my-3 tw-text-lg tw-font-bold">{renderCode(item)}</span>
                        <span className="tw-flex tw-gap-4 tw-text-accents-6">
                            {renderScopeIcons(item).map((icon, i) => (
                                <FontAwesomeIcon icon={icon} key={i} />
                            ))}
                        </span>
                    </div>
                )
            },
            {
                uid: 'meta',
                name: t('clubMember.preview.vouchers.columns.info'),
                renderer: (_, item) => <VoucherStatus voucher={item} />
            },
            {
                uid: 'price',
                name: t('clubMember.preview.vouchers.columns.paymentInfo'),
                renderer: (_, item?) =>
                    item.paymentId && (
                        <PaymentStatusButton paymentId={item.paymentId} paymentStatus={item.paymentStatus} />
                    )
            },
            {
                uid: 'actions',
                align: 'end',
                name: t('clubMember.preview.vouchers.columns.options')
            }
        ],
        [t, renderTypeName, renderCode, renderScopeIcons]
    );

    const showVoucherValueManager = useCallback(
        (voucher: Voucher) => {
            addModal({
                title: t('clubMember.preview.vouchers.manageTitle'),
                content: (id) => (
                    <VoucherValueManager
                        voucher={voucher}
                        callback={() => {
                            asyncListRef?.current?.reload();
                            removeModal(id);
                        }}
                    />
                ),
                show: true,
                size: 60
            });
        },
        [t, addModal, removeModal]
    );

    const handleCopyCode = useCallback(
        (voucher: Voucher) => {
            if (!navigator.clipboard) {
                return;
            }

            navigator.clipboard
                .writeText(voucher.code.toUpperCase())
                .then((r) => alert(t('clubMember.preview.vouchers.codeCopied')));
        },
        [t]
    );

    const handleDownload = useCallback(
        (voucher: Voucher) => {
            void api({
                url: `club/${clubSlug}/voucher/${voucher.id}/download`,
                method: 'GET',
                responseType: 'blob',
                onSuccess: (data) =>
                    download(data, t('clubMember.preview.vouchers.downloadFilename', { id: voucher.id }))
            });
        },
        [download, clubSlug, t]
    );

    return (
        <DataTableContainer>
            <DataTable
                className="tw-h-full"
                fetchUrl={`club/${clubSlug}/voucher`}
                columns={columns}
                initialFilterState={{ user_email: { value: member.email } }}
                asyncListRef={asyncListRef}
                rowActions={(item: Voucher) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={faPowerOff}
                            tooltip={
                                item.is_active
                                    ? t('clubMember.preview.vouchers.deactivate')
                                    : t('clubMember.preview.vouchers.activate')
                            }
                            color={item.is_active ? 'success' : 'error'}
                            onPress={() => handleToggleVoucherStatus(item)}
                        />
                        <TableActionButton
                            icon={faPaste}
                            tooltip={t('clubMember.preview.vouchers.copyCode')}
                            onPress={() => handleCopyCode(item)}
                        />
                        {item.type !== 'discount' && (
                            <>
                                <TableActionButton
                                    icon={faFileDownload}
                                    tooltip={t('clubMember.preview.vouchers.download')}
                                    onPress={() => handleDownload(item)}
                                />
                                {item.value > item.value_used && (
                                    <TableActionButton
                                        icon={faTags}
                                        tooltip={t('clubMember.preview.vouchers.manageValue')}
                                        color="warning"
                                        onPress={() => showVoucherValueManager(item)}
                                    />
                                )}
                            </>
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default ClubMemberVoucherList;
